import Check from '@mui/icons-material/Check';
import { Chip, Grid2 as Grid, Stack, Typography } from '@mui/material';
import { Button } from 'framework/ui/mui-builder';
import type { FormDefinition } from '../format/form-definition';
import { BuilderProvider, type BuilderContextValue, type DrawerContent } from './context';
import type { Edit } from './edits';
import { save, useUnsavedReport } from './saving';
import { useState } from 'react';
import IconButton from '@mui/material/IconButton';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import { BuilderMainDrawer } from './drawer/BuilderMainDrawer';
import { Canvas } from './canvas/Canvas';
import { FieldsPalette } from './fieldsPalette/FieldsPalette';
import { Tile } from './fieldsPalette/tiles';
import { createElementIdGenerator, findElement } from '../format/elements';
import { getInsertionPosition } from './elementInsertion';
import { useBuilderState } from './state';

interface BuilderProps {
  form: FormDefinition;
  saved: FormDefinition | null;
  formLink?: string;
  onSave: (definition: FormDefinition) => Promise<void>;
  onUnsavedChange?: (hasUnsavedChanges: boolean) => void;
}

type DrawerState = { content: DrawerContent; open: boolean };

export const Builder = (props: BuilderProps) => {
  const { onSave, onUnsavedChange } = props;
  const [state, dispatch] = useBuilderState(props.form, props.saved);
  const edit = (change: Edit) => dispatch({ type: 'edit', edit: change });
  useUnsavedReport(state.unsaved, onUnsavedChange);
  const isNew = state.saved === null;
  const needsSave = isNew || state.unsaved;

  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const openDrawer = (content: DrawerContent) => setDrawer({ content, open: true });
  const closeDrawer = () => setDrawer(shown => (shown?.open ? { ...shown, open: false } : shown));
  const drawerOpen =
    drawer?.open === true &&
    (drawer.content.kind === 'form' || findElement(state.form, drawer.content.id) !== undefined);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const openSettings = () => {
    setSelectedId(null);
    openDrawer({ kind: 'form' });
  };
  const openElement = (id: string) => {
    setSelectedId(id);
    openDrawer({ kind: 'element', id });
  };
  const clickEmptyCanvas = () => {
    setSelectedId(null);
    closeDrawer();
  };

  const [scrollTo, setScrollTo] = useState<string | null>(null);
  const add = (tile: Tile) => {
    const element = tile.create(createElementIdGenerator(state.form));
    edit({ kind: 'addElement', element, at: getInsertionPosition(state.form, element, selectedId) });
    setSelectedId(element.id);
    setScrollTo(element.id);
  };

  const builder: BuilderContextValue = {
    form: state.form,
    edit,
    selectedId,
    select: setSelectedId,
    openElement,
    drawer: drawerOpen ? drawer.content : null,
  };

  return (
    <BuilderProvider value={builder}>
      <Grid
        container
        wrap="nowrap"
        spacing={1.5}
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <Grid
          size="grow"
          sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, px: 2, minWidth: 320, minHeight: 0 }}
        >
          <Stack
            direction="row"
            alignItems={'center'}
            justifyContent={'space-between'}
            sx={{
              px: 2,
              flexShrink: 0,
              backgroundColor: 'common.white',
              borderRadius: 2,
              py: 1,
            }}
          >
            <Stack direction="row" gap={1}>
              <Typography noWrap>{state.form.title}</Typography>
              {needsSave ? (
                <Chip
                  size="small"
                  color={state.failed ? 'error' : 'warning'}
                  label={state.failed ? "Couldn't save" : isNew ? 'Not saved yet' : 'Unsaved changes'}
                />
              ) : (
                <Chip size="small" color="success" icon={<Check />} label="Saved" />
              )}
            </Stack>
            <IconButton onClick={openSettings}>
              <SettingsIcon />
            </IconButton>
          </Stack>
          <Canvas
            onOpenSettings={openSettings}
            onEmptyClick={clickEmptyCanvas}
            scrollTo={scrollTo}
            footer={
              needsSave && (
                <Button
                  variant="contained"
                  loading={state.saving}
                  loadingPosition="start"
                  onClick={event => {
                    event.stopPropagation();
                    save(state, dispatch, onSave);
                  }}
                >
                  {state.saving ? 'Saving…' : state.failed ? 'Try again' : 'Save'}
                </Button>
              )
            }
          />
        </Grid>
        <Grid size="auto" sx={{ width: 560, flexShrink: 0, minHeight: 0 }}>
          <FieldsPalette onAdd={add} />
        </Grid>
      </Grid>
      <BuilderMainDrawer open={drawerOpen} content={drawer?.content ?? null} onClose={closeDrawer} />
    </BuilderProvider>
  );
};
